/**
 * Suspense wrapper for lazy-loaded routes
 * Provides consistent loading UI and handles module loading errors.
 *
 * NOTE: "Importing a module script failed" is commonly caused by stale service-worker caches
 * after a deployment. We auto-clear SW caches once and reload.
 */

import { Suspense, ReactNode, Component, ErrorInfo } from "react";
import { LoadingSkeleton } from "./LoadingSkeleton";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

interface RouteSuspenseProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

const RECOVERY_FLAG_KEY = "cataleon_module_recovery_attempted";

async function clearServiceWorkerCaches(): Promise<void> {
  try {
    if (typeof caches !== "undefined") {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch (e) {
    console.warn("Failed clearing caches", e);
  }

  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch (e) {
    console.warn("Failed unregistering service workers", e);
  }
}

class ModuleErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Module loading error:", error, errorInfo);

    const msg = error?.message ?? "";
    const isModuleError =
      msg.includes("module") ||
      msg.includes("script") ||
      msg.includes("chunk") ||
      msg.includes("import");

    // Auto-recover once per tab/session by clearing SW caches and reloading.
    if (isModuleError) {
      try {
        const attempted = sessionStorage.getItem(RECOVERY_FLAG_KEY);
        if (!attempted) {
          sessionStorage.setItem(RECOVERY_FLAG_KEY, "1");
          void (async () => {
            await clearServiceWorkerCaches();
            window.location.reload();
          })();
        }
      } catch {
        // ignore
      }
    }
  }

  handleHardRefresh = async () => {
    await clearServiceWorkerCaches();
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const msg = this.state.error?.message ?? "";
      const isModuleError =
        msg.includes("module") ||
        msg.includes("script") ||
        msg.includes("chunk") ||
        msg.includes("import");

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
          <div className="text-center max-w-md">
            <div className="mb-6 p-4 rounded-full bg-muted/50 inline-flex">
              <RefreshCw className="h-8 w-8 text-muted-foreground" />
            </div>
            <h2 className="text-xl font-semibold mb-2">
              {isModuleError ? "Loading Error" : "Something went wrong"}
            </h2>
            <p className="text-muted-foreground mb-6">
              {isModuleError
                ? "This is usually caused by an outdated cached app version. We'll clear the cache and reload."
                : "An unexpected error occurred. Please try refreshing the page."}
            </p>
            <Button onClick={this.handleHardRefresh} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Clear Cache & Reload
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export const RouteSuspense = ({ children }: RouteSuspenseProps) => {
  return (
    <ModuleErrorBoundary>
      <Suspense fallback={<LoadingSkeleton />}>{children}</Suspense>
    </ModuleErrorBoundary>
  );
};
